<script setup lang="ts">
import type { GradeCode } from '~/models/Grade'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import arrayUtils from '~/utils/arrayUtils'
import useSchoolYear from '~/composables/useSchoolYear'
import { useAssignTask } from '~/composables/useAssignTask'
import { EXAM_TAG } from '~/composables/useAssignTags'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import AssignedTaskCard from '~/components/cards/AssignedTaskCard.vue'

const { selectedGrades } = defineProps<{ selectedGrades: GradeCode[] }>()

const { t } = useI18n()
const { intersect } = arrayUtils()
const { currentSchoolYearCodeApi } = useSchoolYear()

const { tasks, isLoading, isError } = useAssignTask({
  tags: [EXAM_TAG],
  additionalParams: computed(() => ({
    schoolYear: currentSchoolYearCodeApi
  }))
})

const filteredTasks = computed(() => (tasks?.value ?? []).filter(task => !task.grades
  || !selectedGrades.length
  || intersect(selectedGrades, task.grades).length > 0))
</script>

<template>
  <section class="space-y-2">
    <h2
      class="text-2xl font-bold"
      v-text="t('assign.planned')"
    />
    <AuSkeletonWrapper v-if="isLoading">
      <AuSkeleton height="70px" />
      <AuSkeleton height="70px" />
      <AuSkeleton height="70px" />
    </AuSkeletonWrapper>
    <div
      v-else-if="isError"
      v-text="t('assign.error')"
    />
    <div v-else-if="!filteredTasks?.length">
      <AuCallout
        variant="info"
        :title="t('assign.noTasks')"
      >
        <p class="flex flex-col space-y-2">
          <span v-text="t('assign.noTasksInfo1')" />
          <span v-text="t('assign.noTasksInfo2')" />
        </p>
      </AuCallout>
    </div>
    <ul
      v-else
      class="grid grid-cols-1 gap-2"
    >
      <AssignedTaskCard
        v-for="task in filteredTasks"
        :key="`task-${task.taskId}`"
        :task
      />
    </ul>
  </section>
</template>
